import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download } from "lucide-react";
import api, { errMsg, downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { php } from "@/lib/format";
import { PageHeader, MarketSelect, PeopleSelect, SelectBox, Panel } from "@/components/common/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { dateRange, DateFilter } from "@/pages/Dashboard";

function T({ head, rows, testid }) {
  return (
    <div className="overflow-x-auto" data-testid={testid}>
      <Table>
        <TableHeader><TableRow>{head.map((h, i) => <TableHead key={h} className={i ? "text-right" : ""}>{h}</TableHead>)}</TableRow></TableHeader>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={i}>{r.map((c, j) => <TableCell key={j} className={j ? "text-right num text-sm whitespace-nowrap" : "text-sm"}>{c}</TableCell>)}</TableRow>
          ))}
        </TableBody>
      </Table>
      {rows.length === 0 && <div className="text-sm text-slate-400 py-6 text-center">No data</div>}
    </div>
  );
}

export default function Reports() {
  const { can, meta, marketName } = useAuth();
  const [f, setF] = useState({ market: "", assigned_to: "", source: "", location: "" });
  const [preset, setPreset] = useState("");
  const [custom, setCustom] = useState({ from: "", to: "" });
  const [d, setD] = useState(null);

  const params = useCallback(() => Object.fromEntries(Object.entries({ ...f, ...dateRange(preset, custom) }).filter(([, v]) => v)), [f, preset, custom]);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/reports/summary", { params: params() });
      setD(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  }, [params]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const set = (k) => (v) => setF({ ...f, [k]: v?.target ? v.target.value : v });

  return (
    <div data-testid="reports-page">
      <PageHeader eyebrow="Analysis" title="Reports" subtitle={`Computed from stored records${d?.scope === "own" ? " · limited to your assigned leads" : ""}.`}>
        {can("reports.export") && <Button variant="outline" onClick={() => downloadFile("/export/leads.csv", "salescore_leads.csv", params()).catch((e) => toast.error(errMsg(e)))} data-testid="reports-export-button"><Download className="h-4 w-4 mr-1" />Export leads CSV</Button>}
      </PageHeader>
      <div className="flex flex-wrap gap-2 mb-5">
        <MarketSelect value={f.market} onChange={set("market")} />
        {(can("leads.view_all") || can("reports.view_all")) && <PeopleSelect value={f.assigned_to} onChange={set("assigned_to")} />}
        <SelectBox value={f.source} onChange={set("source")} testid="report-source-filter" allLabel="All sources" options={(meta.settings.lead_sources || []).map((s) => ({ value: s, label: s }))} />
        <Input placeholder="Location" className="w-40 bg-white" value={f.location} onChange={set("location")} data-testid="report-location-filter" />
        <DateFilter preset={preset} setPreset={setPreset} custom={custom} setCustom={setCustom} />
      </div>
      {!d ? <div className="text-slate-400">Loading…</div> : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[["Leads", d.kpis.total_leads], ["Open pipeline (estimate)", php(d.kpis.pipeline_value)], ["Won sales (actual)", php(d.kpis.won_value)], ["Win rate (won ÷ closed)", `${d.kpis.win_rate}%`]].map(([k, v]) => (
              <div key={k} className="bg-white border rounded-md p-4" data-testid={`report-kpi-${k.split(" ")[0].toLowerCase()}`}><div className="eyebrow">{k}</div><div className="num text-xl font-bold mt-1">{v}</div></div>
            ))}
          </div>
          <div className="grid lg:grid-cols-2 gap-4">
            <Panel title="Performance by salesperson">
              <T testid="report-by-salesperson" head={["Salesperson", "Leads", "Open", "Pipeline", "Won", "Won value", "Lost"]} rows={d.by_executive.map((r) => [r.name, r.leads, r.open, php(r.pipeline), r.won_count, php(r.won_value), r.lost])} />
            </Panel>
            <Panel title="Lead source performance">
              <T testid="report-by-source" head={["Source", "Leads", "Won", "Won value", "Conv."]} rows={d.by_source.map((r) => [r.source, r.count, r.won, php(r.won_value), `${r.count ? Math.round((r.won / r.count) * 100) : 0}%`])} />
            </Panel>
            {Object.entries(d.funnel).map(([m, stages]) => (
              <Panel key={m} title={`Stage conversion — ${marketName(m)}`}>
                <T testid={`report-funnel-${m}`} head={["Stage", "Currently in", "Reached", "% reached", "Value in stage"]} rows={stages.map((s) => [s.name, s.current, s.reached, `${s.pct}%`, php(s.value)])} />
              </Panel>
            ))}
            <Panel title="Lost opportunities by reason">
              <T testid="report-lost-reasons" head={["Reason", "Count"]} rows={d.lost_reasons.map((r) => [r.reason, r.count])} />
            </Panel>
            <Panel title="Leads by location (top 15)">
              <T testid="report-by-location" head={["Location", "Leads", "Open pipeline"]} rows={d.by_location.map((r) => [r.location, r.count, php(r.pipeline)])} />
            </Panel>
          </div>
        </div>
      )}
    </div>
  );
}
